import React, { useState, useEffect, useReducer } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Card,
  CardContent,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import DataArrayIcon from '@mui/icons-material/DataArray';
import InsightIcon from '@mui/icons-material/Insights';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SimilarIcon from '@mui/icons-material/FindReplace';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import LoadingButton from '../../Shared/Loading/index.tsx';
import { useRecoilState } from 'recoil';
import {
  multiKnowledgeBaseSearchAtom,
  SingleKnowledgeBaseSearchAtom,
} from '../../../Services/_states/VectorDatabase/index.ts';

const MultiKnowledgeBaseSearch = ({
  organizationId,
  availableKnowledgeBases = [],
  preselectedKBs = [],
  currentKBName = '',
}) => {
  const { showSnackBar } = useSnackBar();
  const {
    searchKnowledgeBase,
    searchMultiKnowledgeBase,
    retrieveChunks,
    getSimilarChunks,
    listKnowledgeBases,
  } = AiActions();

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [knowledgeBases, setKnowledgeBases] = useState(availableKnowledgeBases);
  const [selectedKBs, setSelectedKBs] = useState(preselectedKBs);
  const [searchMode, setSearchMode] = useState('multi'); // 'single' or 'multi'
  const [selectedSingleKB, setSelectedSingleKB] = useState('');
  const [kbsLoading, setKbsLoading] = useState(false);
  const [searchSummary, setSearchSummary] = useState([]);
  const [searchMetadata, setSearchMetadata] = useState({});
  const [fullResponseData, setFullResponseData] = useRecoilState(multiKnowledgeBaseSearchAtom);
  const [SingleResponseData, setSingleResponseData] = useRecoilState(SingleKnowledgeBaseSearchAtom);
  const [showRawResponse, setShowRawResponse] = useState(false);
  // Advanced filters
  const [searchFilters, setSearchFilters] = useState({
    topK: 10,
    scoreThreshold: 0.7,
    includeMetadata: true,
  });

  // Load knowledge bases if not provided
  useEffect(() => {
    if (searchMode === 'single' && organizationId) {
      loadKnowledgeBases();
    }
  }, [organizationId, searchMode]);

  const loadKnowledgeBases = async () => {
    setKbsLoading(true);
    try {
      const response = await listKnowledgeBases(organizationId);
      // Handle different possible response structures
      let kbList = [];
      if (response?.knowledge_bases) {
        kbList = response.knowledge_bases;
      } else if (Array.isArray(response)) {
        kbList = response;
      } else if (response?.data?.knowledge_bases) {
        kbList = response.data.knowledge_bases;
      }

      setKnowledgeBases(kbList);

      if (kbList.length === 0) {
        showSnackBar('No knowledge bases found for this organization', 'info');
      }
    } catch (error) {
      showSnackBar('Failed to load knowledge bases', 'error');
    } finally {
      setKbsLoading(false);
    }
  };

  const handleKBSelection = (kbId, checked) => {
    if (checked) {
      setSelectedKBs((prev) => [...prev, kbId]);
    } else {
      setSelectedKBs((prev) => prev.filter((id) => id !== kbId));
    }
  };

  const handleSelectAllKBs = () => {
    const allKBIds = knowledgeBases.map((kb) => kb.knowledge_base_id || kb.id);
    setSelectedKBs(allKBIds);
  };

  const handleClearAllKBs = () => {
    setSelectedKBs([]);
  };

  const cleanSearchText = (text) => {
    if (!text) return '';

    return text
      .replace(/^\d+\s+remialcsid\s+eht\s+daer\s+esaelP\*\s*/i, '')
      .replace(/\d+\s+remialcsid\s+eht\s+daer\s+esaelP\*/gi, '')
      .replace(/\s+/g, ' ')
      .replace(/[^\w\s.,!?;:()\-€₹$]/g, '')
      .trim();
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      showSnackBar('Please enter a search query', 'warning');
      return;
    }

    if (searchMode === 'multi' && selectedKBs.length === 0) {
      showSnackBar('Please select at least one knowledge base', 'warning');
      return;
    }

    if (searchMode === 'single' && !selectedSingleKB) {
      showSnackBar('Please select a knowledge base', 'warning');
      return;
    }

    setSearchLoading(true);
    try {
      const searchData = {
        query: searchQuery.trim(),
        top_k: searchFilters.topK,
        score_threshold: searchFilters.scoreThreshold,
        include_metadata: searchFilters.includeMetadata,
      };

      let response;
      let processedResults = [];

      if (searchMode === 'multi') {
        // Multi-KB search - matches your API structure exactly
        response = await searchMultiKnowledgeBase(organizationId, {
          ...searchData,
          knowledge_base_ids: selectedKBs,
        });

        setFullResponseData(response?.results);
        setSearchSummary(response?.search_summary || []);
        setSearchMetadata(response?.search_metadata || {});
      } else {
        // Single KB search
        response = await searchKnowledgeBase(organizationId, selectedSingleKB, searchData);
        // setFullResponseData(response);
        setSingleResponseData(response?.results);

        if (response?.results && Array.isArray(response.results)) {
          const kbName = getKnowledgeBaseName(selectedSingleKB);
          processedResults = response.results.map((result, index) => ({
            ...result,
            displayText: cleanSearchText(result.text || result.content || ''),
            chunk_id: result.chunk_id || result.vector_id || `chunk_${index}`,
            similarity_score: result.similarity_score || result.score || 0,
            knowledge_base_id: selectedSingleKB,
            knowledge_base_name: kbName,
          }));
        }
      }

      setSearchResults(processedResults);

      if (processedResults.length === 0) {
      } else {
        showSnackBar(
          `Found ${processedResults.length} results across ${searchMode === 'multi' ? selectedKBs.length : 1} knowledge base(s)`,
          'success'
        );
      }
    } catch (err) {
      showSnackBar('Search failed. Please try again.', 'error');
      setSearchResults([]);
      setFullResponseData([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleGetSimilarChunks = async (result) => {
    if (!result.chunk_id || !result.knowledge_base_id) {
      showSnackBar('Cannot find similar chunks - missing chunk or knowledge base ID', 'warning');
      return;
    }

    try {
      const response = await getSimilarChunks(
        organizationId,
        result.knowledge_base_id,
        result.chunk_id,
        {
          top_k: 5,
          include_metadata: true,
        }
      );

      if (response?.results && response.results.length > 0) {
        // Add similar results to current results with a visual indicator
        const similarResults = response.results.map((similar) => ({
          ...similar,
          displayText: cleanSearchText(similar.text || similar.content || ''),
          isSimilar: true,
          originalQuery: result.text?.substring(0, 50) + '...',
          knowledge_base_id: result.knowledge_base_id,
          knowledge_base_name: result.knowledge_base_name,
        }));

        setSearchResults((prev) => [...prev, ...similarResults]);
        showSnackBar(`Found ${response.results.length} similar chunks`, 'success');
      } else {
        showSnackBar('No similar chunks found', 'info');
      }
    } catch (err) {
      showSnackBar('Failed to get similar chunks', 'error');
    }
  };

  const getKnowledgeBaseName = (kbId) => {
    const kb = knowledgeBases.find((kb) => (kb.knowledge_base_id || kb.id) === kbId);
    return kb?.name || 'Unknown KB';
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchSummary([]);
    setSearchMetadata({});
    // setFullResponseData([]);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackBar('Content copied to clipboard', 'success');
  };

  // Raw Response Viewer Component
  const RawResponseViewer = () => {
    const renderValue = (value, key = '') => {
      if (value === null || value === undefined) {
        return <span style={{ color: '#6b7280' }}>null</span>;
      }

      if (typeof value === 'boolean') {
        return <span style={{ color: value ? '#059669' : '#dc2626' }}>{String(value)}</span>;
      }

      if (typeof value === 'number') {
        return <span style={{ color: '#0ea5e9' }}>{value}</span>;
      }

      if (typeof value === 'string') {
        if (key === 'text' && value.length > 200) {
          return (
            <div style={{ maxHeight: '100px', overflow: 'auto', fontSize: '0.85rem' }}>{value}</div>
          );
        }
        return <span style={{ color: '#065f46' }}>"{value}"</span>;
      }

      if (Array.isArray(value)) {
        return (
          <div style={{ marginLeft: '20px' }}>
            <div style={{ color: '#374151', fontWeight: 600 }}>[{value.length} items]</div>
            {value.map((item, index) => (
              <div key={index} style={{ marginLeft: '15px', marginTop: '8px' }}>
                <span style={{ color: '#6b7280' }}>{index}:</span> {renderValue(item)}
              </div>
            ))}
          </div>
        );
      }

      if (typeof value === 'object') {
        return (
          <div style={{ marginLeft: '20px' }}>
            {Object.entries(value).map(([k, v]) => (
              <div key={k} style={{ marginTop: '8px' }}>
                <span style={{ color: '#1f2937', fontWeight: 600 }}>{k}:</span> {renderValue(v, k)}
              </div>
            ))}
          </div>
        );
      }

      return String(value);
    };

    return (
      <Dialog
        open={showRawResponse}
        onClose={() => setShowRawResponse(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6">Raw API Response</Typography>
          <IconButton onClick={() => setShowRawResponse(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Paper sx={{ p: 2, bgcolor: '#f8fafc', maxHeight: '70vh', overflow: 'auto' }}>
            <pre
              style={{
                fontFamily: 'Monaco, Consolas, monospace',
                fontSize: '12px',
                lineHeight: '1.4',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {fullResponseData ? renderValue(fullResponseData) : 'No response data available'}
            </pre>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              copyToClipboard(JSON.stringify(fullResponseData, null, 2));
            }}
            startIcon={<ContentCopyIcon />}
          >
            Copy JSON
          </Button>
          <Button onClick={() => setShowRawResponse(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FindInPageIcon color="primary" />
        Multi-Knowledge Base Search
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search across multiple knowledge bases using semantic vector search
      </Typography>

      <Grid container spacing={3}>
        {/* Search Configuration Panel */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Search Mode Selection */}
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Search Mode
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Search Mode</InputLabel>
                <Select
                  value={searchMode}
                  label="Search Mode"
                  onChange={(e) => setSearchMode(e.target.value)}
                >
                  <MenuItem value="multi">Multi Knowledge Base</MenuItem>
                  <MenuItem value="single">Single Knowledge Base</MenuItem>
                </Select>
              </FormControl>
            </Paper>

            {/* Knowledge Base Selection */}
            {searchMode === 'multi' ? (
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Select Knowledge Bases ({selectedKBs.length})
                  </Typography>
                  <Box>
                    <Button size="small" onClick={handleSelectAllKBs} sx={{ mr: 1 }}>
                      All
                    </Button>
                    <Button size="small" onClick={handleClearAllKBs}>
                      Clear
                    </Button>
                  </Box>
                </Box>

                {kbsLoading ? (
                  <Box display="flex" alignItems="center" gap={2} py={2}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading knowledge bases...</Typography>
                  </Box>
                ) : (
                  <Stack spacing={1} sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {knowledgeBases.map((kb) => (
                      <FormControlLabel
                        key={kb.knowledge_base_id || kb.id}
                        control={
                          <Checkbox
                            checked={selectedKBs.includes(kb.knowledge_base_id || kb.id)}
                            onChange={(e) =>
                              handleKBSelection(kb.knowledge_base_id || kb.id, e.target.checked)
                            }
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {kb.name}
                              {currentKBName === kb.name && (
                                <Chip label="Current" size="small" color="primary" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                            {kb.description && (
                              <Typography variant="caption" color="text.secondary">
                                {kb.description.substring(0, 60)}...
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    ))}
                  </Stack>
                )}

                {!kbsLoading && knowledgeBases.length === 0 && (
                  <Alert severity="info">
                    No knowledge bases available. Create some knowledge bases first.
                  </Alert>
                )}
              </Paper>
            ) : (
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Select Knowledge Base
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Knowledge Base</InputLabel>
                  <Select
                    value={selectedSingleKB}
                    label="Knowledge Base"
                    onChange={(e) => setSelectedSingleKB(e.target.value)}
                  >
                    {knowledgeBases.map((kb) => (
                      <MenuItem
                        key={kb.knowledge_base_id || kb.id}
                        value={kb.knowledge_base_id || kb.id}
                      >
                        {kb.name}
                        {currentKBName === kb.name && ' (Current)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            )}

            {/* Advanced Filters */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="search-filters-content"
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <TuneIcon color="primary" />
                  <Typography variant="h6">Advanced Filters</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <TextField
                    label="Max Results"
                    type="number"
                    size="small"
                    value={searchFilters.topK}
                    onChange={(e) =>
                      setSearchFilters((prev) => ({
                        ...prev,
                        topK: parseInt(e.target.value) || 10,
                      }))
                    }
                    inputProps={{ min: 1, max: 50 }}
                    helperText="Maximum number of results to return"
                  />
                  <TextField
                    label="Score Threshold"
                    type="number"
                    size="small"
                    value={searchFilters.scoreThreshold}
                    onChange={(e) =>
                      setSearchFilters((prev) => ({
                        ...prev,
                        scoreThreshold: parseFloat(e.target.value) || 0.7,
                      }))
                    }
                    inputProps={{ min: 0, max: 1, step: 0.1 }}
                    helperText="Minimum similarity score (0-1)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={searchFilters.includeMetadata}
                        onChange={(e) =>
                          setSearchFilters((prev) => ({
                            ...prev,
                            includeMetadata: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Include metadata in results"
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Search Summary */}
            {searchSummary.length > 0 && (
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <StorageIcon color="primary" />
                  Search Summary
                </Typography>
                <Stack spacing={1}>
                  {searchSummary.map((summary, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        bgcolor: summary.status === 'success' ? '#f0fdf4' : '#fef2f2',
                        borderRadius: 1,
                        border: `1px solid ${summary.status === 'success' ? '#bbf7d0' : '#fecaca'}`,
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" fontWeight={600}>
                          {summary.knowledge_base_name}
                        </Typography>
                        <CheckCircleIcon
                          fontSize="small"
                          color={summary.status === 'success' ? 'success' : 'error'}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Found {summary.results_found} results (from {summary.total_before_filtering}{' '}
                        total)
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                {searchMetadata.embedding_model && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
                    <Typography variant="caption" color="text.secondary">
                      Model: {searchMetadata.embedding_model} • Searched{' '}
                      {searchMetadata.knowledge_bases_searched} KB(s) • Total found:{' '}
                      {searchMetadata.total_found_before_limit}
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Stack>
        </Grid>

        {/* Search Interface and Results */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={3}>
            {/* Search Input */}
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <TextField
                fullWidth
                placeholder="Enter your search query..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                <LoadingButton
                  buttonName="Search"
                  isLoading={searchLoading}
                  isDisabled={searchLoading}
                  onClickButton={handleSearch}
                />
                {searchResults.length > 0 && (
                  <>
                    <Button variant="outlined" onClick={clearSearch}>
                      Clear Results
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => setShowRawResponse(true)}
                      sx={{ textTransform: 'none' }}
                    >
                      View Raw Response
                    </Button>
                  </>
                )}
                <Typography variant="body2" color="text.secondary">
                  {searchResults.length > 0 && `${searchResults.length} results found`}
                </Typography>
              </Box>
            </Paper>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <DataArrayIcon color="primary" />
                  Search Results
                </Typography>
              </Box>
            )}
          </Stack>
            <Grid
              item
              xs={12}
              md={12}
              lg={12}
              sx={{
              width: { sm: 'auto', md: '100%', lg: '100%' },
              mt: 4,
              maxHeight: '300px',
              overflowY: 'auto',
              }}
            >
              <Stack spacing={2}>
                {(fullResponseData?.length ? fullResponseData : SingleResponseData)?.map(
                  (r: any, index: number) => {
                    const fileName = r.file_name || r.metadata?.file_name || 'Unknown File';
                    const fileType = (r.file_type || r.metadata?.file_type || 'FILE').toUpperCase();
                    const chunkIndex = r.chunk_index ?? r.metadata?.chunk_index ?? index;
                    const kbName =
                      r.knowledge_base_name || getKnowledgeBaseName?.(r.knowledge_base_id);
                    const createdAt = r.created_at || r.metadata?.created_at;
                    const sim = r.similarity_score ?? r.score ?? 0;
                    const body = cleanSearchText(r.text || r.content || '');

                    return (
                      <Card
                        key={r.vector_id || r.chunk_id || index}
                        sx={{ borderLeft: '1px solid #e0e0e0', position: 'relative' }} // removed blue stripe
                      >
                        <CardContent>
                          {/* Header: file / kb / score */}
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            mb={1.5}
                          >
                            <Box flex={1}>
                              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                                {fileName}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                              >
                                {/* removed Badge wrapper to avoid overlap */}
                                <Chip
                                  label={kbName || 'Unknown KB'}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                                <Chip
                                  label={fileType}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  Chunk {chunkIndex}
                                </Typography>
                                {r.vector_id && (
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {String(r.vector_id).substring(0, 8)}...
                                  </Typography>
                                )}
                              </Stack>
                            </Box>

                            <Chip
                              label={`${(sim * 100).toFixed(1)}%`}
                              size="small"
                              color={sim > 0.8 ? 'success' : sim > 0.7 ? 'warning' : 'default'}
                              sx={{ ml: 2 }}
                            />
                          </Box>

                          {/* Full text (no clamp) */}
                          {body && (
                            <Typography
                              variant="body2"
                              color="text.primary"
                              sx={{ mb: 2, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
                            >
                              {body}
                            </Typography>
                          )}

                          {/* Meta line */}
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {createdAt && (
                                <>Created: {new Date(createdAt).toLocaleDateString()}</>
                              )}
                              {r.source_type && <> • Type: {r.source_type}</>}
                              {r.metadata?.chunk_length && (
                                <> • Length: {r.metadata.chunk_length} chars</>
                              )}
                            </Typography>
                          </Box>

                          {/* ALL KEYS / VALUES */}
                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: '#f9fafb',
                              borderRadius: 1,
                              border: '1px solid #eee',
                              mb: 1.5,
                            }}
                          >
                            {Object.entries(r).map(([key, value]) => (
                              <Box
                                key={key}
                                sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}
                              >
                                <Typography sx={{ fontWeight: 600, minWidth: 160 }}>
                                  {key}:
                                </Typography>
                                <Typography
                                  sx={{
                                    ml: 1,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem',
                                  }}
                                >
                                  {typeof value === 'object'
                                    ? JSON.stringify(value, null, 2)
                                    : String(value)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          {/* Actions */}
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<ContentCopyIcon />}
                              onClick={() => copyToClipboard(body)}
                              sx={{ textTransform: 'none' }}
                            >
                              Copy
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  }
                )}
              </Stack>
            </Grid>
          
        </Grid>
      </Grid>
      <RawResponseViewer />
    </Box>
  );
};

export default MultiKnowledgeBaseSearch;
